import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { StepperOrientation, StepperSelectionEvent } from '@angular/cdk/stepper';
import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Observable, map, takeUntil } from 'rxjs';
import { BaseComponent } from './../../../core/components/base/base.component';
import { FeedbackService } from './../../../core/services/feedback.service';
import { ScrollPositionService } from './../../../core/services/scroll-position.service';
import { PatientSatisfactionSurveyService } from 'src/app/core/services/patient-satisfaction-survey.service';

@Component({
  selector: 'app-patient-satisfaction-survey',
  templateUrl: './patient-satisfaction-survey.component.html',
  styleUrls: ['./patient-satisfaction-survey.component.scss']
})
export class PatientSatisfactionSurveyComponent extends BaseComponent implements OnInit {
  @ViewChild('scrollableParent', { static: false }) scrollableParent!: ElementRef;

  isLinear = false;
  surveyToken?: string;
  showCompleted: boolean = false;
  patientName: string = '';
  group?: any;
  surveyDetails?: any;
  errorText: string = '';
  isLoading: boolean = false;

  surveyForm!: FormGroup;
  stepperOrientation!: Observable<StepperOrientation>;
  isMobile!: boolean;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private patientSatisfactionSurveyService: PatientSatisfactionSurveyService,
    private cd: ChangeDetectorRef,
    private scrollService: ScrollPositionService,
    breakpointObserver: BreakpointObserver,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);

    this.stepperOrientation = breakpointObserver
      .observe('(min-width: 800px)')
      .pipe(map(({ matches }) => (matches ? 'horizontal' : 'vertical')));

    breakpointObserver.observe([Breakpoints.HandsetPortrait, Breakpoints.TabletPortrait])
      .subscribe(result => {
        this.isMobile = result.matches;
      });
  }

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.surveyToken = params['token'];
      this.loadSurveyDetails();
    });
  }

  private loadSurveyDetails() {
    const sub = this.patientSatisfactionSurveyService.validateSurveyToken(this.surveyToken!)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          if (result.data) {
            this.surveyDetails = result.data;
            this.group = this.surveyDetails.Group;
            this.patientName = this.surveyDetails.Patient.name;
            this.createSurveyForm(this.surveyDetails.surveyConfig);
            this.cd.detectChanges();
          } else {
            this.errorText = 'No survey data available';
            this.showToastMessage('No survey data available');
          }
          sub.unsubscribe();
        },
        error: (error) => {
          console.error(error);
          const errorMessage = error?.error?.message || 'An unknown error occurred.';
          this.errorText = `Failed to load survey: ${errorMessage}`;
          this.showToastMessage(`Failed to load survey details: ${errorMessage}`);
          sub.unsubscribe();
        }
      });
  }
  

  private createSurveyForm(surveyConfig: any) {
    const formControls: { [key: string]: any } = {};

    // Create form controls for each section and question
    surveyConfig.sections.forEach((section: any) => {
      section.questions.forEach((question: any) => {
        const validators = question.required ? [Validators.required] : [];
        formControls[question.id] = ['', validators];
      });
    });

    this.surveyForm = this.formBuilder.group(formControls);
  }

  get surveySections(): any[] {
    return this.surveyDetails?.surveyConfig?.sections || [];
  }

  onStepChange(event: StepperSelectionEvent) {
    if (this.isMobile) {
      const stepIndex = event.selectedIndex;
      const stepContentId = `step-${stepIndex}-content`;
      setTimeout(() => {
        this.scrollService.scrollTo(stepContentId);
      }, 0);
    }
  }

  formIsValid(): boolean {
    return this.surveyForm.valid;
  }

  isSectionCompleted(section: any): boolean {
    if (!section || !section.questions) {
      return false;
    }

    // Check if all required questions in this section are answered
    for (const question of section.questions) {
      if (question.required) {
        const control = this.surveyForm.get(question.id);
        if (!control || !control.value || (typeof control.value === 'string' && control.value.trim() === '')) {
          return false;
        }
      }
    }

    return true;
  }

  submit() {
    if (this.isLoading) return;

    this.isLoading = true;

    if (!this.surveyForm.valid) {
      this.showToastMessage("Please complete all required questions.");
      this.isLoading = false;
      return;
    }

    const responses = this.surveyForm.value;

    const sub = this.patientSatisfactionSurveyService.submitSurveyByToken(this.surveyToken!, responses)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Thank you for your feedback! Your survey has been submitted successfully.');
          this.patientName = this.surveyDetails.Patient.name;
          this.showCompleted = true;
          this.isLoading = false;
          sub.unsubscribe();
        },
        error: (error) => {
          this.isLoading = false;
          console.error(error);
          this.showToastMessage('Failed to submit survey. Please try again.');
          sub.unsubscribe();
        }
      });
  }
} 